import { useEffect, useState } from 'react'
import { sendGet, sendPost, sendPostFormData } from '../../hooks/use-fetch'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import CircularProgress from '@mui/material/CircularProgress'
import { useNavigate, useParams } from 'react-router-dom'
import { TextFieldWithLabel } from '../TextField'
import { DatePickerWithLabel } from '../DatePicker'
import FormGroup from '@mui/material/FormGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { SelectWithLabel } from '../SelectWithLabel'
import { type location, type lookup, type postError } from '../../core/types'
import { AsYouType } from 'libphonenumber-js'
import { styled } from '@mui/material/styles'
import IconButton from '@mui/material/IconButton'
import { BannerModal } from '../Modal/BannerModal'
import S3Avatar from '../S3Image/S3Avatar'
import { toast } from 'react-toastify'
import { CustomLabelWithToolTip } from '../CustomLabel/Index'
import ClearIcon from '@mui/icons-material/Clear'
import { LocationDetails } from '../Cards/LocationCard/LocationDetails'
import { MapStateName } from '../Cards/LocationCard/mapStateName'
import { Breadcrumbs, Link } from '@mui/material'

interface CaregiverEditModel {
  caregiverId?: number
  firstName: string
  middleName?: string
  lastName: string
  dateOfBirth: Date | null
  primaryPhone: string
  secondaryPhone: string
  email: string
  primaryLanguageId?: string | number
  relationshipId: string | number
  isFinanciallyResponsible: boolean
  livesWithChild: boolean
  portalView: boolean
  portalModify: boolean
  s3Key?: string
  preferredContactMethod: string | number
  highestLevelOfEducationId?: string | number
  childFirstName: string
}

export const CaregiverEdit = (): JSX.Element => {
  const params = useParams()
  const nav = useNavigate()
  const [caregiver, setCaregiver] = useState<CaregiverEditModel | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [contactMethods, setContactMethods] = useState<lookup[]>([])
  const [languages, setLanguages] = useState<lookup[]>([])
  const [educationLevels, setEducationLevels] = useState<lookup[]>([])
  const [relationships, setRelationships] = useState<lookup[]>([])
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const [originalCaregiverName, setOriginalCaregiverName] = useState<string>('')
  const [address, setAddress] = useState<location>({
    id: 0,
    address: '',
    city: '',
    state: '',
    zipCode: '',
    apartment: '',
    note: ''
  })

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (params.caregiverId !== undefined) {
        const { response, success } = await sendGet(
          `/CaregiverDetails/GetEditData?caregiverId=${params.caregiverId ?? 0}&childId=${params.childId ?? 0}`, {})

        if (success) {
          setCaregiver(response.response.caregiver)
          response.response.address !== null && setAddress(response.response.address)
          setOriginalCaregiverName(`${response.response.caregiver.firstName as string} ${response.response.caregiver.lastName as string}`)
        }
      } else {
        const { response, success } = await sendGet(
          `/CaregiverDetails/GetChildFirstName?childId=${params.childId ?? 0}`, {})

        success && setCaregiver({
          firstName: '',
          middleName: '',
          lastName: '',
          dateOfBirth: null,
          primaryPhone: '',
          secondaryPhone: '',
          email: '',
          primaryLanguageId: undefined,
          relationshipId: 0,
          isFinanciallyResponsible: false,
          livesWithChild: false,
          portalView: false,
          portalModify: false,
          s3Key: undefined,
          preferredContactMethod: 0,
          highestLevelOfEducationId: undefined,
          childFirstName: response.response
        })
      }
    }

    if (params.childId !== undefined) {
      void fetchData()
    }
  }, [params.childId, params.caregiverId])

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const { response, error } = await sendGet('/Language/GetLookup?sort=true', {})
      if (error[0] === '') {
        setLanguages(response)
      }
      const { response: eduLevels, error: err } = await sendGet('/EducationLevel/GetLookup?sort=true', {})
      if (err[0] === '') {
        setEducationLevels(eduLevels)
      }
      const { response: relations, error: err1 } = await sendGet('/Relationship/GetLookup?sort=true', {})
      if (err1[0] === '') {
        setRelationships(relations)
      }
      const { response: contactmethods, error: err2 } = await sendGet('/ContactMethod/GetAll', {})
      if (err2[0] === '') {
        setContactMethods(contactmethods)
      }
    }
    void fetchData()
  }, [])

  /* https://v5.mui.com/material-ui/react-button/#file-upload */
  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1
  })

  const handleSave = async (): Promise<void> => {
    if (caregiver !== null && (caregiver.firstName === '' ||
      caregiver.lastName === '' ||
      caregiver.relationshipId === 0 ||
      caregiver.preferredContactMethod === 0 ||
      (!caregiver.livesWithChild && (address.address === '' ||
        address.city === '' || address.state === '' ||
        address.zipCode === '')))) {
      toast.error('Please complete all required fields')
      return
    }

    const { response: rsp } = await sendPost(`/CaregiverDetails/PostEditData?childId=${params.childId ?? ''}`, {
      caregiver: caregiver ?? {},
      address: caregiver?.livesWithChild === false ? address : undefined
    })
    const result: { newRecordID: number, errors: postError[], success: boolean } = rsp
    if (result.success) {
      if (imageFile !== null) {
        const imageFormData = new FormData()
        imageFormData.append('file', imageFile)
        imageFormData.append('caregiverId', result.newRecordID.toString())
        imageFormData.append('childId', params.childId ?? '')
        const { success } = await sendPostFormData('/CaregiverDetails/UploadImage', imageFormData)
        !success && toast.error('Error uploading image')
      }

      nav(-1)
    } else {
      result.errors?.forEach((e) => {
        toast.error(e.error)
      })
    }
  }

  const handleDelete = async (): Promise<void> => {
    const { success } = await sendPost(`/CaregiverDetails/Delete?caregiverId=${caregiver?.caregiverId ?? ''}&childId=${params.childId ?? ''}`, {})
    if (success) {
      nav(-1)
    }
  }
  const goBack = (): void => {
    nav(-1)
  }

  return <>
    {params.caregiverId === undefined
      ? <Typography sx={{ display: 'flex', fontWeight: 'bold', paddingBottom: 2 }} variant='h5'>Add Caregiver Information</Typography>
      : <Box sx={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 2 }}>
        <Typography sx={{ display: 'flex', fontWeight: 'bold', paddingBottom: 0 }} variant='h5'>Edit Caregiver Information - {originalCaregiverName}</Typography>
      </Box>
    }
    <Breadcrumbs>
      <Link onClick={goBack} sx={{ textTransform: 'none', color: 'inherit', textDecorationLine: 'none', cursor: 'pointer' }}>Return to Care Network Details</Link>
    </Breadcrumbs>

    <Paper elevation={0} square={false} sx={{ padding: 2 }}>
      <Typography variant='h6' gutterBottom>Caregiver Details</Typography>
      {caregiver === null
        ? <CircularProgress />
        : <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                label='First Name'
                name='firstName'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, firstName: e.target.value })
                }}
                value={caregiver.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                showRequiredText
                label='Middle Name'
                name='middleName'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, middleName: e.target.value })
                }}
                value={caregiver.middleName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                label='Last Name'
                name='lastName'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, lastName: e.target.value })
                }}
                value={caregiver.lastName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePickerWithLabel
                showRequiredText
                label='Date of Birth'
                value={caregiver.dateOfBirth}
                onChange={(newValue) => {
                  setCaregiver({ ...caregiver, dateOfBirth: newValue })
                }}
                name={'dateOfBirth'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectWithLabel
                width='100%'
                name='relationshipId'
                onChange={(e) => {
                  e.target.value !== null && setCaregiver({ ...caregiver, relationshipId: e.target.value })
                }}
                value={caregiver.relationshipId}
                options={relationships}
                labelContent={<CustomLabelWithToolTip
                  name=''
                  isdId={0}
                  defaultLabelText={`Relationship to ${caregiver.childFirstName}`}
                  defaultTooltipText={'The value selected here, along with the options for "Lives with" and "Financially Responsible" are used to determine eligibility during the enrollment process'}
                />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormGroup row sx={{ display: 'flex', height: '100%', alignContent: 'center', paddingLeft: { xs: 0, lg: 2 } }}>
                <FormControlLabel
                  label={`Lives with ${caregiver.childFirstName}`}
                  control={<Checkbox checked={caregiver.livesWithChild} onChange={(e) => {
                    setCaregiver({ ...caregiver, livesWithChild: e.target.checked })
                  }}
                  />}
                />
                <FormControlLabel
                  control={<Checkbox checked={caregiver.isFinanciallyResponsible}
                    onChange={(e) => {
                      setCaregiver({ ...caregiver, isFinanciallyResponsible: e.target.checked })
                    }}
                  />}
                  label={`Financially responsible for ${caregiver.childFirstName}`}
                />
              </FormGroup>
            </Grid>
            {!caregiver.livesWithChild && <Grid item xs={12}>
              <LocationDetails
                onChange={(e) => {
                  setAddress({ ...address, [e.target.name]: e.target.value })
                }}
                onPlaceSelected={async (place: Microsoft.Maps.ISuggestionResult) => {
                  const newplace: location = { ...address }
                  newplace.address = place.address.addressLine
                  newplace.city = place.address.locality
                  newplace.apartment = ''
                  newplace.state = MapStateName(place.address.adminDistrict)
                  newplace.zipCode = place.address.postalCode
                  setAddress(newplace)
                }}
                editData={address}
                childID={null}
                showHomelessOption={false}
                hidePrimary={true}
              />
            </Grid>}
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                showRequiredText
                label='Phone'
                name='primaryPhone'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, primaryPhone: e.target.value })
                }}
                value={new AsYouType('US').input(caregiver.primaryPhone ?? '')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                showRequiredText
                label='Secondary Phone'
                name='secondaryPhone'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, secondaryPhone: e.target.value })
                }}
                value={new AsYouType('US').input(caregiver.secondaryPhone ?? '')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextFieldWithLabel
                showRequiredText
                name='email'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, email: e.target.value })
                }}
                value={caregiver.email}
                labelContent={<CustomLabelWithToolTip
                  name=''
                  isdId={0}
                  defaultLabelText='Email'
                  defaultTooltipText={'Providing an email is a prerequisite for having access to the caregiver portal.'}
                />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              {(caregiver?.email?.length ?? 0) > 0 && <FormGroup row sx={{ display: 'flex', height: '100%', alignContent: 'center', paddingLeft: { xs: 0, lg: 2 } }}>
                <FormControlLabel
                  label='Portal View'
                  control={<Checkbox checked={caregiver.portalView}
                    onChange={(e) => {
                      setCaregiver({ ...caregiver, portalView: e.target.checked, portalModify: false })
                    }}
                  />}
                />
                <FormControlLabel
                  label='Portal Modify'
                  control={<Checkbox checked={caregiver.portalModify}
                    onChange={(e) => {
                      setCaregiver({ ...caregiver, portalModify: e.target.checked, portalView: e.target.checked ? true : caregiver.portalView })
                    }}
                  />}
                />
              </FormGroup>}
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectWithLabel
                showRequiredText
                includeNoneOption
                noneOptionText='(none)'
                width='100%'
                label='Primary Language (if not English)'
                name='primaryLanguageId'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, primaryLanguageId: e.target.value })
                }}
                value={caregiver.primaryLanguageId}
                options={languages}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Stack direction='row' spacing={1} alignItems='center' height='100%'>
                {caregiver.s3Key === undefined
                  ? <Avatar sx={{ height: '50px', width: '50px' }} />
                  : <S3Avatar imageKey={caregiver.s3Key} height='50px' width='50px' />}
                <Stack direction='column' spacing={0} alignItems='flex-start'>
                  <Button
                    component='label'
                    role={undefined}
                    variant='text'
                    tabIndex={-1}
                  >
                    {caregiver.s3Key === undefined ? 'Click here to add' : 'Click here to update'}
                    <VisuallyHiddenInput
                      type='file'
                      onChange={(e) => {
                        setImageFile(e.target.files?.[0] ?? null)
                      }}
                    />
                  </Button>
                  {imageFile !== null && <Stack direction='row' spacing={0.5} alignItems='center' sx={{ pl: 1 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ padding: 0 }}>{imageFile.name}</Typography>
                    <IconButton
                      size='small'
                      color='inherit'
                      onClick={() => {
                        setImageFile(null)
                      }}
                    >
                      <ClearIcon fontSize="inherit" />
                    </IconButton>
                  </Stack>}
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectWithLabel
                width='100%'
                name='preferredContactMethod'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, preferredContactMethod: e.target.value })
                }}
                value={caregiver.preferredContactMethod}
                options={contactMethods}
                labelContent={<CustomLabelWithToolTip
                  name=''
                  isdId={0}
                  defaultLabelText='Preferred Contact Method'
                  defaultTooltipText={'This defines how the MiECC team should contact this caregiver'}
                />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SelectWithLabel
                showRequiredText
                includeNoneOption
                width='100%'
                label='Highest Level of Education'
                name='highestLevelOfEducationId'
                onChange={(e) => {
                  setCaregiver({ ...caregiver, highestLevelOfEducationId: e.target.value === null ? undefined : e.target.value })
                }}
                value={caregiver.highestLevelOfEducationId}
                options={educationLevels}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant='outlined'
              color='inherit'
              onClick={() => {
                nav(-1)
              }}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='error'
              onClick={() => {
                setShowDeleteModal(true)
              }}
            >
              Delete
            </Button>
            <Button
              variant='contained'
              color='primary'
              onClick={handleSave}
            >
              Save Caregiver
            </Button>
          </Box>
        </>
      }
    </Paper >

    <BannerModal
      open={showDeleteModal}
      onClose={() => { setShowDeleteModal(false) }}
      onConfirm={handleDelete}
      content={<>Are you sure you want to remove this caregiver from this child? Doing so will revoke all of their access.</>}
      title='Delete Caregiver'
      confirmButtonText='Delete'
      confirmButtonColor='error'
      cancelButtonText='Cancel'
    />
  </>
}
